import React from 'react';
import type { Category } from '@/types/category';
import type { Country } from '@/types/country';
import type { Topic } from '@/types/topic';

export type ArticleFormGroupedBy = 'country' | 'topic';
export type ArticleFormType = 'monologue' | 'dialogue' | '';

export interface ArticleCreateFormFieldsValue {
  groupedBy: ArticleFormGroupedBy;
  country: string;
  category: string;
  topicCode: string;
  tags: string[];
  articleTopic: string;
  description: string;
  articleType: ArticleFormType | '';
}

type FieldName = keyof ArticleCreateFormFieldsValue;

interface ArticleCreateFormFieldsProps {
  idPrefix: string;
  value: ArticleCreateFormFieldsValue;
  countries: Country[];
  categories: Category[];
  topics: Topic[];
  tags: Array<{ tag: string }>;
  isAdmin: boolean;
  onChangeField: (field: FieldName, value: string | string[]) => void;
  onOpenAddCountry?: () => void;
  onOpenAddCategory?: () => void;
  onOpenAddTopic?: () => void;
  onOpenAddTag?: () => void;
  descriptionLabel?: string;
  descriptionPlaceholder?: string;
  descriptionRows?: number;
  showDescriptionHelp?: boolean;
}

export const ArticleCreateFormFields: React.FC<ArticleCreateFormFieldsProps> = ({
  idPrefix,
  value,
  countries,
  categories,
  topics,
  tags,
  isAdmin,
  onChangeField,
  onOpenAddCountry,
  onOpenAddCategory,
  onOpenAddTopic,
  onOpenAddTag,
  descriptionLabel = 'Situation',
  descriptionPlaceholder = 'Enter article content or description',
  descriptionRows = 8,
  showDescriptionHelp = true,
}) => {
  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-8 mb-4">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">Grouped By</label>
          <div className="flex items-center gap-6">
            <label className="inline-flex items-center text-sm">
              <input
                type="radio"
                name={`${idPrefix}-groupedBy`}
                value="country"
                checked={value.groupedBy === 'country'}
                onChange={() => onChangeField('groupedBy', 'country')}
                className="form-radio h-4 w-4 text-blue-600 border-gray-300"
              />
              <span className="ml-2">Country</span>
            </label>

            <label className="inline-flex items-center text-sm">
              <input
                type="radio"
                name={`${idPrefix}-groupedBy`}
                value="topic"
                checked={value.groupedBy === 'topic'}
                onChange={() => onChangeField('groupedBy', 'topic')}
                className="form-radio h-4 w-4 text-blue-600 border-gray-300"
              />
              <span className="ml-2">Topic</span>
            </label>
          </div>
        </div>

        <div className="ml-auto flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">
            Article Type<span className="text-red-500">*</span>
          </label>
          <div className="flex items-center gap-6">
            <label className="inline-flex items-center text-sm">
              <input
                type="radio"
                name={`${idPrefix}-articleType`}
                value="monologue"
                checked={value.articleType === 'monologue'}
                onChange={() => onChangeField('articleType', 'monologue')}
                className="form-radio h-4 w-4 text-blue-600 border-gray-300"
              />
              <span className="ml-2">Monologue</span>
            </label>

            <label className="inline-flex items-center text-sm">
              <input
                type="radio"
                name={`${idPrefix}-articleType`}
                value="dialogue"
                checked={value.articleType === 'dialogue'}
                onChange={() => onChangeField('articleType', 'dialogue')}
                className="form-radio h-4 w-4 text-blue-600 border-gray-300"
              />
              <span className="ml-2">Dialogue</span>
            </label>
          </div>
        </div>
      </div>

      {value.groupedBy === 'country' && (
        <div>
          <div className="flex justify-between items-center mb-2">
            <label
              htmlFor={`${idPrefix}-country`}
              className="block text-sm font-medium text-gray-700"
            >
              Country <span className="text-red-500">*</span>
            </label>
            {isAdmin && onOpenAddCountry && (
              <button
                type="button"
                onClick={onOpenAddCountry}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                + Add Country
              </button>
            )}
          </div>
          <select
            id={`${idPrefix}-country`}
            value={value.country}
            onChange={e => onChangeField('country', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          >
            <option value="">Select a country</option>
            {countries.map(country => (
              <option key={country.countryCode} value={country.countryCode}>
                {country.countryNameEN}
              </option>
            ))}
          </select>
        </div>
      )}

      {value.groupedBy === 'country' && (
        <div>
          <div className="flex justify-between items-center mb-2">
            <label
              htmlFor={`${idPrefix}-category`}
              className="block text-sm font-medium text-gray-700"
            >
              Category <span className="text-red-500">*</span>
            </label>
            {isAdmin && onOpenAddCategory && (
              <button
                type="button"
                onClick={onOpenAddCategory}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                + Add Category
              </button>
            )}
          </div>
          <select
            id={`${idPrefix}-category`}
            value={value.category}
            onChange={e => onChangeField('category', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          >
            <option value="">Select a category</option>
            {categories.map(cat => (
              <option key={cat.categoryCode} value={cat.categoryCode}>
                {cat.categoryNameEN}
              </option>
            ))}
          </select>
        </div>
      )}

      {value.groupedBy === 'topic' && (
        <div>
          <div className="flex justify-between items-center mb-2">
            <label
              htmlFor={`${idPrefix}-topicCode`}
              className="block text-sm font-medium text-gray-700"
            >
              Topic <span className="text-red-500">*</span>
            </label>
            {isAdmin && onOpenAddTopic && (
              <button
                type="button"
                onClick={onOpenAddTopic}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                + Add Topic
              </button>
            )}
          </div>
          <select
            id={`${idPrefix}-topicCode`}
            value={value.topicCode}
            onChange={e => onChangeField('topicCode', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          >
            <option value="">Select a topic</option>
            {topics.map(topic => (
              <option key={topic.topicCode} value={topic.topicCode}>
                {topic.topicNameEN}
              </option>
            ))}
          </select>
        </div>
      )}

      <div>
        <div className="flex justify-between items-center mb-2">
          <label htmlFor={`${idPrefix}-tags`} className="block text-sm font-medium text-gray-700">
            Tags <span className="text-gray-400 text-xs ml-1">(optional)</span>
          </label>
          {isAdmin && onOpenAddTag && (
            <button
              type="button"
              onClick={onOpenAddTag}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              + Add Tag
            </button>
          )}
        </div>
        <select
          id={`${idPrefix}-tags`}
          multiple
          value={value.tags}
          onChange={e =>
            onChangeField(
              'tags',
              Array.from(e.target.selectedOptions).map(option => option.value)
            )
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {tags.map(tag => (
            <option key={tag.tag} value={tag.tag}>
              {tag.tag}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label
          htmlFor={`${idPrefix}-articleTopic`}
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          Article Topic <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id={`${idPrefix}-articleTopic`}
          value={value.articleTopic}
          onChange={e => onChangeField('articleTopic', e.target.value)}
          placeholder="Enter the main topic or title"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          required
        />
      </div>

      <div>
        <label
          htmlFor={`${idPrefix}-description`}
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          {descriptionLabel} <span className="text-red-500">*</span>
        </label>
        <textarea
          id={`${idPrefix}-description`}
          value={value.description}
          onChange={e => onChangeField('description', e.target.value)}
          placeholder={descriptionPlaceholder}
          rows={descriptionRows}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
          required
        />
        {showDescriptionHelp && (
          <p className="text-sm text-gray-500 mt-1">
            Provide the initial article content. This will be used as the base text for A, B, and C
            level versions.
          </p>
        )}
      </div>
    </>
  );
};
